import React, { useState, useEffect, useRef } from "react";
import { Send, Loader2, Package, BarChart3, CheckCircle, AlertTriangle } from "lucide-react";
import { API_CONFIG } from '../config/api';

// Debug logging
console.log("=== INVENTORY AGENT DEBUG ===");
console.log("API_CONFIG:", API_CONFIG);
console.log("INVENTORY_API:", API_CONFIG.INVENTORY_API);
console.log("Type:", typeof API_CONFIG.INVENTORY_API);

const InventoryAgent = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Generate session ID once per page load (33+ characters required)
  const [sessionId] = useState(() => {
    return `inventory-session-${Date.now()}-${Math.random().toString(36).substr(2, 15)}`;
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const pushMessage = (msg) => setMessages((prev) => [...prev, msg]);

  const updateMessageText = (id, newText, isThinking = false, inventoryData = null, metrics = null) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === id ? { ...m, text: newText, isThinking, inventoryData, metrics } : m
      )
    );
  };

  const extractInventoryData = (text) => {
    try {
      console.log("=== EXTRACTING INVENTORY DATA ===");
      console.log("Text:", text);
      
      // Try to parse JSON response first
      try {
        const jsonData = JSON.parse(text);
        if (jsonData.items || jsonData.inventory || jsonData.products) {
          return jsonData.items || jsonData.inventory || jsonData.products;
        }
      } catch (e) {
        console.log("Not JSON, trying text parsing");
      }
      
      // Look for employee details pattern
      const employeeMatch = text.match(/Employee\s+(CRT\d+)\s+details:\s*\n-\s*Name:\s*([^\n]+)\s*\n-\s*Active employee:\s*([^\n]+)\s*\n-\s*Assets allocated:\s*([\s\S]*?)(?:\n\n|$)/i);
      
      if (employeeMatch) {
        const [, employeeId, name, activeStatus, assetsText] = employeeMatch;
        
        // Parse assets
        const assets = [];
        const assetMatches = assetsText.matchAll(/•\s*([^(]+)\s*\(([^,]+),\s*serial ID:\s*([^,]+),\s*assigned on\s*([^)]+)\)/g);
        
        for (const assetMatch of assetMatches) {
          const [, assetType, quantity, serialId, assignedDate] = assetMatch;
          assets.push({
            type: assetType.trim(),
            quantity: quantity.trim(),
            serialId: serialId.trim(),
            assignedDate: assignedDate.trim()
          });
        }
        
        return [{
          employeeId: employeeId,
          name: name.trim(),
          active: activeStatus.trim().toLowerCase() === 'yes',
          assets: assets,
          type: 'employee'
        }];
      }
      
      // Look for structured inventory data in text (fallback)
      const lines = text.split('\n');
      const items = [];
      
      for (const line of lines) {
        // Match patterns like "Product: Widget A, Stock: 150, Status: In Stock"
        const match = line.match(/(?:Product|Item):\s*([^,]+),?\s*(?:Stock|Quantity):\s*(\d+),?\s*(?:Status):\s*([^,\n]+)/i);
        if (match) {
          items.push({
            name: match[1].trim(),
            stock: parseInt(match[2]),
            status: match[3].trim(),
            type: 'product'
          });
        }
      }
      
      return items.length > 0 ? items : null;
    } catch (err) {
      console.error("Error extracting inventory data:", err);
      return null;
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim()) return;

    const text = inputValue;
    setInputValue("");

    const userId = `u-${Date.now()}`;
    pushMessage({ id: userId, text, sender: "user" });

    const thinkingId = `b-${Date.now()}`;
    pushMessage({
      id: thinkingId,
      text: "Processing inventory request...",
      sender: "bot",
      isThinking: true,
    });

    setIsLoading(true);

    try {
      console.log("=== SENDING INVENTORY REQUEST TO AGENTCORE RUNTIME ===");
      console.log("URL:", API_CONFIG.INVENTORY_API);
      console.log("Session ID:", sessionId);
      console.log("Body:", { prompt: text });

      if (!API_CONFIG.INVENTORY_API || API_CONFIG.INVENTORY_API === 'undefined') {
        throw new Error("INVENTORY_API is undefined! Check config.");
      }

      const response = await fetch(API_CONFIG.INVENTORY_API, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "X-Session-Id": sessionId,
        },
        mode: 'cors',  // Explicitly set CORS mode
        credentials: 'omit',  // Don't send credentials
        body: JSON.stringify({ 
          prompt: text,
        }),
      });

      console.log("=== RESPONSE STATUS ===", response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("=== RAW API GATEWAY DATA ===", data);
      
      // Parse the response from API Gateway + Lambda
      let final = "";
      let responseMetrics = null;
      
      if (data.success && data.response) {
        let response_text = data.response;
        
        // Extract metrics if available
        if (data.metrics) {
          responseMetrics = data.metrics;
          console.log("=== EXTRACTED METRICS ===", responseMetrics);
        }
        
        // Try to extract text from the Python dict format
        try {
          console.log("=== PARSING RESPONSE ===");
          console.log("Raw response_text:", response_text);
          
          // Method 1: Try regex to extract text from Python dict format
          const textMatch = response_text.match(/'text':\s*'([^']+(?:\\.[^']*)*)'}/);
          if (textMatch) {
            final = textMatch[1].replace(/\\n/g, '\n').replace(/\\'/g, "'");
            console.log("✅ Extracted via regex:", final);
          } else {
            // Method 2: Try to convert Python dict to JSON and parse
            try {
              // Replace single quotes with double quotes for JSON parsing
              let jsonString = response_text.replace(/'/g, '"');
              const parsed = JSON.parse(jsonString);
              
              if (parsed.role === 'assistant' && parsed.content && parsed.content[0] && parsed.content[0].text) {
                final = parsed.content[0].text;
                console.log("✅ Extracted via JSON parsing:", final);
              } else {
                final = response_text;
                console.log("⚠️ JSON parsed but no expected structure, using raw");
              }
            } catch (jsonError) {
              console.log("⚠️ JSON parsing failed:", jsonError);
              final = response_text;
            }
          }
        } catch (parse_error) {
          console.log("Response parsing failed, using raw response:", parse_error);
          final = response_text;
        }
      } else {
        final = data.error || "No response received from agent.";
      }

      console.log("=== FINAL TEXT ===", final);

      // Clean up the text
      if (typeof final === 'string') {
        final = final.replace(/\\n/g, "\n");
        final = final.replace(/\\"/g, '"');
        final = final.replace(/```json\n?|\n?```/g, '').trim();
      }

      // Extract inventory data if present
      const inventoryData = extractInventoryData(final);
      console.log("=== EXTRACTED INVENTORY DATA ===", inventoryData);

      updateMessageText(thinkingId, final, false, inventoryData, responseMetrics);
    } catch (err) {
      console.error("=== ERROR ===", err);
      let errorMessage = `⚠️ Error connecting to inventory agent: ${err.message}`;
      
      if (err.message.includes('401')) {
        errorMessage += "\n\nThis might be an authentication issue. The AgentCore Runtime may require proper authentication tokens.";
      } else if (err.message.includes('403')) {
        errorMessage += "\n\nAccess forbidden. Please check IAM permissions for the AgentCore Runtime.";
      } else if (err.message.includes('404')) {
        errorMessage += "\n\nRuntime not found. Please verify the runtime ARN and URL construction.";
      }
      
      updateMessageText(thinkingId, errorMessage, false);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('in stock') || statusLower.includes('available')) {
      return 'text-green-600 bg-green-100';
    } else if (statusLower.includes('low') || statusLower.includes('warning')) {
      return 'text-yellow-600 bg-yellow-100';
    } else if (statusLower.includes('out') || statusLower.includes('empty')) {
      return 'text-red-600 bg-red-100';
    }
    return 'text-gray-600 bg-gray-100';
  };

  return (
    <div className="flex-1 flex flex-col bg-gradient-to-b from-gray-50 to-gray-100 relative">
      {/* CHAT AREA */}
      <div className="flex-1 overflow-y-auto p-6">
        {messages.length === 0 ? (
          // BEAUTIFUL EMPTY STATE
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-xl opacity-90">
              <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
                <Package className="w-12 h-12 text-orange-600" />
              </div>

              <h2 className="text-4xl font-semibold text-gray-900 mb-3">
                Inventory Agent
              </h2>
              <p className="text-gray-600 text-lg mb-6">
                Ask about stock levels, product availability, or inventory management.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                <div className="bg-white p-4 rounded-lg shadow-sm border border-orange-200">
                  <h3 className="font-semibold text-gray-900 mb-2">Sample Questions:</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• "What's the current stock level for Widget A?"</li>
                    <li>• "Show me low stock items"</li>
                    <li>• "Update inventory for Product X"</li>
                  </ul>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-orange-200">
                  <h3 className="font-semibold text-gray-900 mb-2">Capabilities:</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Real-time stock checking</li>
                    <li>• Inventory updates</li>
                    <li>• Stock alerts & reports</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto w-full space-y-6">
            {messages.map((msg) => {
              const isUser = msg.sender === "user";

              return (
                <div
                  key={msg.id}
                  className={`flex items-start gap-4 ${
                    isUser ? "justify-end" : "justify-start"
                  }`}
                >
                  {/* Avatar */}
                  {!isUser && (
                    <div className="w-11 h-11 rounded-full bg-orange-600 flex items-center justify-center shadow-md">
                      <Package className="w-6 h-6 text-white" />
                    </div>
                  )}

                  {/* BUBBLE */}
                  <div
                    className={`max-w-2xl px-6 py-5 shadow-md text-[15px] leading-[1.7] font-['system-ui',-apple-system,BlinkMacSystemFont,'Segoe_UI',sans-serif]
                      ${
                        isUser
                          ? "bg-white text-gray-900 rounded-2xl rounded-br-md border border-gray-200"
                          : "bg-white text-gray-800 rounded-2xl rounded-bl-md border border-gray-200"
                      }`}
                  >
                    {msg.isThinking ? (
                      <div className="flex items-center gap-3">
                        <Loader2 className="w-5 h-5 animate-spin text-orange-600" />
                        <span className="text-gray-700 font-medium">Processing inventory request...</span>
                      </div>
                    ) : (
                      <>
                        <div className={`whitespace-pre-wrap ${isUser ? 'text-gray-900' : 'text-gray-800'}`}>
                          {/* Render text with markdown-style bold formatting */}
                          {msg.text.split(/(\*\*[^*]+\*\*)/).map((part, index) => {
                            if (part.startsWith('**') && part.endsWith('**')) {
                              return <strong key={index} className="text-lg font-bold">{part.slice(2, -2)}</strong>;
                            }
                            return part;
                          })}
                        </div>
                        
                        {/* Inventory Data Display */}
                        {msg.inventoryData && msg.inventoryData.length > 0 && (
                          <div className="mt-6 pt-6 border-t border-gray-200">
                            <div className="flex items-center gap-2 mb-4">
                              <BarChart3 className="w-5 h-5 text-orange-600" />
                              <h3 className="font-semibold text-gray-900 text-base">
                                {msg.inventoryData[0].type === 'employee' ? 'Employee Details' : 'Inventory Status'}
                              </h3>
                            </div>
                            <div className="space-y-2.5">
                              {msg.inventoryData.map((item, idx) => (
                                <div key={idx}>
                                  {item.type === 'employee' ? (
                                    // Employee Details Display
                                    <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                                      <div className="flex items-center gap-3 mb-3">
                                        <div className="w-9 h-9 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
                                          <Package className="w-4 h-4" />
                                        </div>
                                        <div>
                                          <p className="font-semibold text-gray-900 text-[15px]">
                                            {item.name} ({item.employeeId})
                                          </p>
                                          <p className="text-sm text-gray-600">
                                            Status: {item.active ? 'Active Employee' : 'Inactive'}
                                          </p>
                                        </div>
                                        <div className="ml-auto">
                                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                            item.active ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100'
                                          }`}>
                                            {item.active ? 'Active' : 'Inactive'}
                                          </span>
                                        </div>
                                      </div>
                                      
                                      {/* Assets */}
                                      {item.assets && item.assets.length > 0 && (
                                        <div className="mt-3 pt-3 border-t border-gray-200">
                                          <h4 className="font-medium text-gray-900 text-sm mb-2">Allocated Assets:</h4>
                                          <div className="space-y-2">
                                            {item.assets.map((asset, assetIdx) => (
                                              <div key={assetIdx} className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200">
                                                <div className="flex items-center gap-2">
                                                  <div className="w-6 h-6 rounded bg-orange-100 flex items-center justify-center">
                                                    <Package className="w-3 h-3 text-orange-600" />
                                                  </div>
                                                  <div>
                                                    <p className="font-medium text-gray-900 text-sm">{asset.type}</p>
                                                    <p className="text-xs text-gray-600">Serial: {asset.serialId}</p>
                                                  </div>
                                                </div>
                                                <div className="text-right">
                                                  <p className="text-sm font-medium text-gray-900">{asset.quantity}</p>
                                                  <p className="text-xs text-gray-600">{asset.assignedDate}</p>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    // Product Inventory Display (existing)
                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-all group cursor-pointer">
                                      <div className="flex items-center gap-3 flex-1">
                                        <div className="w-9 h-9 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
                                          <Package className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1">
                                          <p className="font-semibold text-gray-900 group-hover:text-orange-600 transition text-[15px]">
                                            {item.name}
                                          </p>
                                          <p className="text-sm text-gray-600">
                                            Stock: {item.stock} units
                                          </p>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                                          {item.status}
                                        </span>
                                        {item.status.toLowerCase().includes('in stock') ? (
                                          <CheckCircle className="w-5 h-5 text-green-600" />
                                        ) : (
                                          <AlertTriangle className="w-5 h-5 text-yellow-600" />
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Simple Token Footer */}
                        {msg.metrics && !isUser && (
                          <div className="mt-4 pt-3 border-t border-gray-100">
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <div className="flex items-center gap-4">
                                <span>Input: {msg.metrics.inputTokens?.toLocaleString() || 0} tokens</span>
                                <span>Output: {msg.metrics.outputTokens?.toLocaleString() || 0} tokens</span>
                                <span>Total: {msg.metrics.totalTokens?.toLocaleString() || 0} tokens</span>
                              </div>
                              <span>
                                {msg.metrics.executionTimeMs ? `${msg.metrics.executionTimeMs}ms` : 
                                 msg.metrics.latencyMs ? `${msg.metrics.latencyMs}ms` : 'N/A'}
                              </span>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* USER AVATAR */}
                  {isUser && (
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold text-lg">U</span>
                    </div>
                  )}
                </div>
              );
            })}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* FLOATING INPUT BAR */}
      <div className="w-full flex justify-center py-6">
        <div className="w-[92%] max-w-4xl bg-white/80 backdrop-blur-xl border border-gray-200 rounded-full px-6 py-3 shadow-lg flex items-center gap-3">
          {/* Input */}
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") sendMessage();
            }}
            placeholder="Ask about inventory, stock levels, or product availability..."
            className="flex-1 bg-transparent outline-none text-gray-800 text-base"
          />

          {/* Send Button */}
          <button
            onClick={sendMessage}
            disabled={!inputValue.trim() || isLoading}
            className={`w-11 h-11 flex items-center justify-center rounded-full transition
              ${
                !inputValue.trim() || isLoading
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-orange-600 hover:bg-orange-700 text-white shadow-md"
              }`}
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InventoryAgent;